/**
 * Shared building blocks for the IFC diff summary surfaces: the 3D rail's
 * compact digest (IfcDiffSummary) and the overview modal's sidebar
 * (IfcDiffOverviewSidebar). Pure constants + helpers only — each surface owns
 * its layout.
 */

import type { BuildingIfcViewerSections } from "@/lib/i18n/types/buildings-sections/ifc-viewer";
import type { IfcModelDiff } from "@/stores/ifcModel/repository";

export const DIFF_SEGMENTS = [
  { key: "added", bar: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
  { key: "removed", bar: "bg-rose-500", text: "text-rose-600 dark:text-rose-400" },
  { key: "modified", bar: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
  { key: "unchanged", bar: "bg-slate-400", text: "text-muted-foreground" },
] as const;

export type DiffSegmentKey = (typeof DIFF_SEGMENTS)[number]["key"];

/** Signed prefix shown before a segment's count (+12 Added, −4 Removed, ~9 Modified). */
export const DIFF_SEGMENT_SIGN: Record<DiffSegmentKey, string> = {
  added: "+",
  removed: "−",
  modified: "~",
  unchanged: "",
};

/** Compact numeric formatting for metric tiles (ints stay int, decimals → 1dp). */
export function fmtMetric(n: number | null): string {
  if (n == null) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function changeTypeLabel(key: DiffSegmentKey, tv: BuildingIfcViewerSections): string {
  switch (key) {
    case "added":
      return tv.changeAdded;
    case "removed":
      return tv.changeRemoved;
    case "modified":
      return tv.changeModified;
    default:
      return tv.changeUnchanged;
  }
}

/**
 * No shared elements → the two files almost certainly aren't two versions of
 * one model (e.g. different disciplines or buildings).
 */
export function hasNoOverlap(summary: IfcModelDiff["summary"]): boolean {
  return (
    summary.unchanged === 0 && summary.modified === 0 && summary.added > 0 && summary.removed > 0
  );
}

export const DIFF_METRICS: {
  labelKey:
    | "metricStoreys"
    | "metricSpaces"
    | "metricElements"
    | "metricMaterials"
    | "metricGrossArea"
    | "metricVolume";
  key: keyof IfcModelDiff["metric_deltas"];
}[] = [
  { labelKey: "metricStoreys", key: "storey_count" },
  { labelKey: "metricSpaces", key: "space_count" },
  { labelKey: "metricElements", key: "element_count" },
  { labelKey: "metricMaterials", key: "material_count" },
  { labelKey: "metricGrossArea", key: "gross_area_sqm" },
  { labelKey: "metricVolume", key: "volume_m3" },
];
